import {createAction, createAsyncThunk} from '@reduxjs/toolkit';

import {notify} from './notifications.js';
import {fetchOrdersRequest} from './orders.js';
import {trackEvent} from '@explorer/analytics';
import {
  createFolderOrderRequest,
  getFolderContents,
} from '@explorer/api/folders';

export const PREFIX = 'FOLDERS';

/* Orders */

export const folderOrderSuccess = createAction(
  `${PREFIX}/FOLDER_ORDER_SUCCESS`
);

export const folderOrderError = createAction(`${PREFIX}/FOLDER_ORDER_ERROR`);

export const orderToFolder = createAsyncThunk(
  `${PREFIX}/ORDER_TO_FOLDER`,
  async ({folderId, orders}, {dispatch}) => {
    const results = [];

    for (const order of orders) {
      const result = await createFolderOrderRequest(folderId, order)
        .then(data => {
          trackEvent('Hosted Data Order Placed Successfully', {
            order: data[0],
          });
          return data;
        })
        .catch(err => {
          // API call errored
          dispatch(folderOrderError(err));
          return {error: err};
        });

      await setTimeout(500);

      results.push(result);
    }
    const errors = results.filter(r => r.error);
    const nonErrorResults = results.filter(r => !r.error);

    /* Error handling for upstream errors */
    if (errors.length) {
      dispatch(folderOrderError(errors));
    }

    // All orders errored
    if (errors.length === results.length) {
      dispatch(
        notify({
          severity: 'error',
          message: {i18nKey: 'ordersDialog.errorAllFolderOrdersFailed'},
          closeButton: true,
        })
      );
    }
    // Some orders errored
    else if (errors.length > 0 && errors.length < results.length) {
      dispatch(
        notify({
          severity: 'error',
          message: {i18nKey: 'ordersDialog.errorSomeFolderOrdersFailed'},
          closeButton: true,
        })
      );
    }

    // No orders errored
    if (nonErrorResults.length && errors.length === 0) {
      dispatch(folderOrderSuccess(results));
      dispatch(
        notify({
          message: {i18nKey: 'ordersDialog.folderOrderSuccess'},
          closeButton: true,
        })
      );

      dispatch(fetchOrdersRequest());
    }
  }
);

// Loads a folder contents given a folder id,
// or loads additional content items for a folder if called with an object
export const loadFolderContents = createAsyncThunk(
  `${PREFIX}/LOAD_FOLDER_CONTENTS`,
  loadData => {
    // If an object (loadData variable) is passed to loadFolderContents, it means we are updating folder contents
    // (triggered by fetchNextPage action), and we use the next link url in our
    // getFolderContents request function.
    // Otherwise if loadData variable is a string, we expect it to be a folder ID.

    let folderId;
    let nextUrl;

    if (typeof loadData === 'string') {
      folderId = loadData;
    } else {
      folderId = loadData.id;
      nextUrl = loadData.url;
    }

    return getFolderContents(folderId, nextUrl);
  }
);

export const setActiveFolder = createAction(`${PREFIX}/SET_ACTIVE_FOLDER`);
// Clear active folder (go back to list of folders in sidebar)
export const clearActiveFolder = createAction(`${PREFIX}/CLEAR_ACTIVE_FOLDER`);

// Load the next page of folder content
export const fetchNextPage = () => (dispatch, getState) => {
  const currentFolderId = getState().folders.selectedFolderId;
  const nextPageUrl = getState().folders.folderContentsNext;

  dispatch(loadFolderContents({id: currentFolderId, url: nextPageUrl}));
};

/*  Folders Results List  */
export const setActiveResult = createAction(`${PREFIX}/SET_ACTIVE_RESULT`);

export const selectFolderViewResults = createAction(`${PREFIX}/SELECT_RESULTS`);
export const deselectFolderViewResults = createAction(
  `${PREFIX}/DESELECT_RESULTS`
);

export const selectFolderViewItems = createAction(`${PREFIX}/SELECT_ITEMS`);
export const deselectFolderViewItems = createAction(`${PREFIX}/DESELECT_ITEMS`);

export const resetFolderSelections = createAction(`${PREFIX}/RESET_SELECTIONS`);
